import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

const ThemeContext = createContext(null);

const THEME_KEY = "unitylove_theme";

// Resolve 'system' to actual theme using matchMedia
function resolveTheme(preference) {
  if (preference === "dark") return "dark";
  if (preference === "light") return "light";
  // system
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyToDOM(resolvedTheme) {
  const root = document.documentElement;
  const isDark = resolvedTheme === "dark";

  // Toggle class
  if (isDark) root.classList.add("dark");
  else root.classList.remove("dark");

  // Improve native UI rendering (inputs/scrollbars)
  root.style.colorScheme = isDark ? "dark" : "light";
  
  // Debug helper: makes theme state visible in DOM
  root.dataset.theme = resolvedTheme;
}

function safeReadPreference() {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
    return "system";
  } catch {
    return "system";
  }
}

export function ThemeProvider({ children }) {
  // Preference is the ONLY persisted setting: 'system' | 'light' | 'dark'
  const [preference, setPreference] = useState(() => safeReadPreference());

  // Resolved theme is derived from preference + OS (if system)
  const resolved = useMemo(() => {
    // In SSR-like environments, guard
    if (typeof window === "undefined") return "light";
    return resolveTheme(preference);
  }, [preference]);

  // Apply theme BEFORE paint to prevent "mixed/inverted until refresh"
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    applyToDOM(resolved);

    // Persist preference
    try {
      localStorage.setItem(THEME_KEY, preference);
    } catch {
      // ignore
    }
  }, [preference, resolved]);

  // If preference === 'system', react to OS changes live
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (preference !== "system") return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      // Only re-apply if we are still in system mode
      applyToDOM(resolveTheme("system"));
    };

    // Safari compatibility
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [preference]);

  // Public setter: apply instantly for snappy UI, then update state
  const setTheme = (nextPreference) => {
    const next =
      nextPreference === "light" || nextPreference === "dark" || nextPreference === "system"
        ? nextPreference
        : "system";

    // Apply immediately (feels instant, reduces "needs refresh")
    if (typeof window !== "undefined") {
      const nextResolved = resolveTheme(next);
      applyToDOM(nextResolved);
    }

    setPreference(next);

    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // ignore
    }
  };

  const value = useMemo(
    () => ({
      preference, // 'system' | 'light' | 'dark'
      theme: resolved, // 'light' | 'dark'
      setTheme,
    }),
    [preference, resolved]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}
